import { Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import arrow1 from 'assets/impact-report/arrow1.png';
import arrow2 from 'assets/impact-report/arrow2.png';
import plasticItems from 'assets/impact-report/plastic-items-min.png';
import tapWater from 'assets/impact-report/tap-water-min.jpeg';
import waste from 'assets/impact-report/waste-min.png';

// Create styles
const styles = StyleSheet.create({
  mainCenter: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: 340,
    padding: 40,
  },
  plasticItems: {
    width: 170,
    position: 'absolute',
    top: 0,
    left: -20,
    zIndex: '10',
  },
  tapWater: {
    position: 'absolute',
    bottom: -30,
    left: '38%',
    border: '15px solid #FFD100',
    borderRadius: '100%',
    overflow: 'hidden',
    zIndex: '10',
    width: 140,
    height: 140,
  },
  tapWaterImg: {
    width: 'auto',
    height: '100%',
    borderRadius: 100,
  },
  arrow1: {
    width: 300,
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: '10',
    transform: 'rotate(180deg)',
  },
  arrow2: {
    width: 240,
    position: 'absolute',
    top: 35,
    left: 26,
    opacity: 0.8,
    zIndex: '10',
    transform: 'rotate(230deg)',
  },
  arrow3: {
    width: 300,
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: '10000',
    transform: 'rotate(20deg)',
  },
  centerLeft: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    height: 250,
    paddingTop: 20,
    paddingLeft: 15,
  },
  centerRight: {
    position: 'absolute',
    zIndex: '10',
    top: 20,
    right: 25,
    color: '#53575A',
    fontWeight: 900,
    fontFamily: 'Trebuchet',
    // backgroundColor: '#bbb',
    // width: '50%',
    // height: 250,
  },
  centerCircle: {
    width: 300,
    position: 'absolute',
    top: '50%',
    left: 0,
    zIndex: '10',
    borderRadius: '50%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    textTransform: 'uppercase',
    color: '#53575A',
    fontFamily: 'Trebuchet',
    fontWeight: 'bold',
  },
  impactTextTitile: {
    fontSize: 12,
  },
  impactTextSub: {
    fontSize: 28,
    fontWeight: 1200,
  },
  waste: {
    width: 200,
    height: 200,
    borderRadius: '100%',
    position: 'absolute',
  },
  centerRightText: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: 200,
    height: 100,
    textAlign: 'center',
    textTransform: 'uppercase',
    paddingTop: 25,
  },
  centerRightBottom: {
    position: 'absolute',
    zIndex: '10',
    bottom: -40,
    right: -15,
    color: '#53575A',
    fontWeight: 900,
    fontFamily: 'Trebuchet',
  },
  centerRightBottomText: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: 200,
    textAlign: 'left',
    textTransform: 'uppercase',
    paddingTop: 100,
  },
});

type PdfImpactReportProps = {
  reportData: any;
};

const ImpactSection1 = ({ reportData }: PdfImpactReportProps) => {
  const reportImpact = reportData.impact;

  const ret = reportImpact?.ret || 0;
  let lost = 0;
  if (reportData.details && reportData.details.length > 0) {
    let beg = 0;
    let del = 0;
    let returned = 0;
    let unu = 0;
    let end = 0;

    for (const detail of reportData.details) {
      beg += detail.beg;
      del += detail.del;
      returned += detail.ret;
      unu += detail.unu;
      end += detail.end;
    }

    lost =
      Number(beg || '0') +
      Number(del || '0') -
      Number(unu || '0') -
      (Number(returned || '0') + Number(end || '0'));
  }
  const singleUseItems = ret + lost;

  // Dynamic font sizing based on number length
  const getDynamicFontSize = (number: number) => {
    const numberString = number.toLocaleString();
    const digitCount = numberString.length;

    // More aggressive scaling to prevent cutoff
    if (digitCount <= 4) return 34;
    if (digitCount <= 6) return 28;
    if (digitCount <= 8) return 24;
    if (digitCount <= 10) return 20;
    if (digitCount <= 12) return 18;
    if (digitCount <= 14) return 16;
    return 14; // Very small font for extremely large numbers
  };

  const dynamicFontSize = getDynamicFontSize(singleUseItems);

  return (
    <>
      <View style={styles.mainCenter}>
        <View style={styles.centerLeft}>
          <View style={styles.arrow1}>
            <Image src={arrow1} />
          </View>
          <View style={styles.arrow3}>
            <Image src={arrow1} />
          </View>
          <View style={styles.arrow2}>
            <Image src={arrow2} />
          </View>
          <View style={styles.centerCircle}>
            <View>
              <Text style={styles.impactTextTitile}>Single-Use</Text>
            </View>
            <View>
              <Text style={styles.impactTextTitile}>Items Eliminated</Text>
            </View>
            <View style={{ maxWidth: '280px' }}>
              <Text
                style={[
                  styles.impactTextSub,
                  {
                    fontSize: dynamicFontSize,
                    textAlign: 'center',
                    lineHeight: 1.1,
                  },
                ]}>
                {singleUseItems.toLocaleString() || 0}
              </Text>
            </View>
          </View>
          <View style={styles.plasticItems}>
            <Image src={plasticItems} />
          </View>
          <View style={styles.centerRight}>
            <Image style={styles.waste} src={waste} />
            <View style={styles.centerRightText}>
              <View>
                <Text style={styles.impactTextTitile}>Pounds of</Text>
              </View>
              <View>
                <Text style={styles.impactTextTitile}>waste diverted:</Text>
              </View>
              <View>
                <Text style={styles.impactTextSub}>
                  {reportImpact?.wd?.toLocaleString() || '0'}
                </Text>
              </View>
            </View>
          </View>
          <View style={styles.centerRightBottom}>
            <View style={styles.centerRightBottomText}>
              <View>
                <Text style={styles.impactTextTitile}>Gallons of</Text>
              </View>
              <View>
                <Text style={styles.impactTextTitile}>water conserved:</Text>
              </View>
              <View style={{ marginLeft: '-10px' }}>
                <Text style={styles.impactTextSub}>
                  {reportImpact?.wc?.toLocaleString() || '0'}
                </Text>
              </View>
            </View>
          </View>
          <View style={styles.tapWater}>
            <Image style={styles.tapWaterImg} src={tapWater} />
          </View>
        </View>
      </View>
    </>
  );
};

export default ImpactSection1;
